import classnames from 'classnames'
import { useId, useRef } from 'react'

import ChevronDown from '@src/quince-site-integrated/src/assets/chevron_down.svg'

import { useSlideDownAnimation } from '../hooks/useSlideDownAnimation'
import styles from '../miniPDPProductOptions.module.scss'

export interface MiniPDPSizeSummarySegment {
  label: string
  value?: string
}

export interface MiniPDPCollapsibleOptionsProps {
  summarySegments: MiniPDPSizeSummarySegment[]
  children: React.ReactNode
  isExpandedSize: boolean
  expandSizeMiniPDP: () => void
}

export function MiniPDPCollapsibleOptions({
  summarySegments,
  children,
  isExpandedSize,
  expandSizeMiniPDP,
}: MiniPDPCollapsibleOptionsProps) {
  const panelId = useId()

  const triggerRef = useRef<HTMLButtonElement>(null)
  /** Pixel height of the summary row, captured immediately before opening (animation starts here). */
  const expandFromHeightPxRef = useRef(0)
  const revealInnerRef = useSlideDownAnimation(isExpandedSize, expandFromHeightPxRef)

  const handleExpand = () => {
    expandFromHeightPxRef.current = triggerRef.current?.offsetHeight ?? 0
    expandSizeMiniPDP()
  }

  return (
    <div className={classnames(styles.sizeCollapsible, isExpandedSize && styles.sizeCollapsible_expanded)}>
      {!isExpandedSize ? (
        <button
          ref={triggerRef}
          type="button"
          className={styles.sizeCollapsible__trigger}
          aria-expanded={false}
          aria-controls={panelId}
          onClick={handleExpand}
        >
          <span className={styles.sizeCollapsible__summary}>
            {summarySegments.length > 0 && (
              <ul className={styles.sizeCollapsible__segmentList}>
                {summarySegments.map((segment, index) => (
                  <>
                    <li key={`${segment.label}-${index}`}>
                      <span className={styles.sizeCollapsible__segmentLabel}>{`${segment.label}: `}</span>
                      <span className={styles.sizeCollapsible__segmentValue}>{segment.value}</span>
                    </li>
                    {index < summarySegments.length - 1 && (
                      <span className={styles.sizeCollapsible__segmentSeparator}>|</span>
                    )}
                  </>
                ))}
              </ul>
            )}
          </span>
          <ChevronDown className={styles.sizeCollapsible__chevron} aria-hidden />
        </button>
      ) : (
        <div
          id={panelId}
          className={classnames(styles.sizeCollapsible__reveal, isExpandedSize && styles.sizeCollapsible__reveal_open)}
          aria-hidden={!isExpandedSize}
        >
          <div ref={revealInnerRef} className={styles.sizeCollapsible__revealInner}>
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
